export interface CookieConsentState {
  v: 1;
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  decidedAt: string;
}

export interface CookieConsentPreferences {
  analytics: boolean;
  marketing: boolean;
}

const COOKIE_NAME = 'gsgo_cookie_consent';
const COOKIE_VERSION = 1;
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

function isBrowser(): boolean {
  return typeof document !== 'undefined';
}

function parseCookieValue(raw: string): CookieConsentState | null {
  try {
    const parsed = JSON.parse(raw) as Partial<CookieConsentState>;
    if (
      parsed.v !== COOKIE_VERSION ||
      parsed.necessary !== true ||
      typeof parsed.analytics !== 'boolean' ||
      typeof parsed.marketing !== 'boolean' ||
      typeof parsed.decidedAt !== 'string'
    ) {
      return null;
    }
    return {
      v: 1,
      necessary: true,
      analytics: parsed.analytics,
      marketing: parsed.marketing,
      decidedAt: parsed.decidedAt,
    };
  } catch {
    return null;
  }
}

export function readCookieConsent(): CookieConsentState | null {
  if (!isBrowser()) return null;
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  const encodedPrefix = `${encodeURIComponent(COOKIE_NAME)}=`;
  const match = cookies.find((cookie) => cookie.startsWith(encodedPrefix));
  if (!match) return null;
  const encodedValue = match.slice(encodedPrefix.length);
  const decodedValue = decodeURIComponent(encodedValue);
  return parseCookieValue(decodedValue);
}

export function writeCookieConsent(preferences: CookieConsentPreferences): void {
  if (!isBrowser()) return;
  const payload: CookieConsentState = {
    v: 1,
    necessary: true,
    analytics: preferences.analytics,
    marketing: preferences.marketing,
    decidedAt: new Date().toISOString(),
  };
  const encodedName = encodeURIComponent(COOKIE_NAME);
  const encodedValue = encodeURIComponent(JSON.stringify(payload));
  const secureAttribute = globalThis.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${encodedName}=${encodedValue}; Path=/; Max-Age=${ONE_YEAR_SECONDS}; SameSite=Lax` +
    secureAttribute;
}

export function clearCookieConsent(): void {
  if (!isBrowser()) return;
  const encodedName = encodeURIComponent(COOKIE_NAME);
  document.cookie = `${encodedName}=; Path=/; Max-Age=0; SameSite=Lax`;
}

export function hasConsentDecision(): boolean {
  return readCookieConsent() !== null;
}

export function isAnalyticsAllowed(state?: CookieConsentState | null): boolean {
  const current = state ?? readCookieConsent();
  return current?.analytics === true;
}
